import type { HubChatConfirmAck } from "@lifter/hub-ui/browser-shared";
import {
  type DashboardPendingConfirmation,
  DashboardPendingConfirmationSchema,
  type DecidedApproval,
  hubQueryKeys,
} from "@lifter/hub-ui/browser-shared";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { createLiveSubscriptionStore } from "../live/liveSubscriptionStore";
import { useAgentsQuery } from "./agents";
import { type AgentQueryOptions, useHubApi } from "./context";
import { useMeQuery } from "./me";

type PendingConfirmationChange =
  | { type: "entry"; confirmation: DashboardPendingConfirmation }
  | { type: "remove"; confirmationId: string };

const RemoveEventSchema = z.object({ confirmationId: z.string().min(1) }).passthrough();
const ReadyEventSchema = z.object({}).passthrough();
const INITIAL_CONFIRMATIONS = new Map<string, DashboardPendingConfirmation>();

const pendingConfirmationStore = createLiveSubscriptionStore<
  string,
  PendingConfirmationChange,
  Map<string, DashboardPendingConfirmation>
>({
  keyOf: (eventsUrl) => eventsUrl,
  connect: (eventsUrl, emit, ready, reset, fail) => {
    const source = new EventSource(eventsUrl, { withCredentials: true });
    const onEntry = (event: MessageEvent<string>) => {
      const confirmation = parseEventData(DashboardPendingConfirmationSchema, event.data);
      if (confirmation) emit({ type: "entry", confirmation });
    };
    const onRemove = (event: MessageEvent<string>) => {
      const removal = parseEventData(RemoveEventSchema, event.data);
      if (removal) emit({ type: "remove", confirmationId: removal.confirmationId });
    };
    const onReady = (event: MessageEvent<string>) => {
      if (parseEventData(ReadyEventSchema, event.data)) ready();
    };
    source.onopen = reset;
    source.onerror = fail;
    source.addEventListener("entry", onEntry as EventListener);
    source.addEventListener("remove", onRemove as EventListener);
    source.addEventListener("ready", onReady as EventListener);
    return () => {
      source.onopen = null;
      source.onerror = null;
      source.removeEventListener("entry", onEntry as EventListener);
      source.removeEventListener("remove", onRemove as EventListener);
      source.removeEventListener("ready", onReady as EventListener);
      source.close();
    };
  },
  reduce: (state, change) => {
    const next = new Map(state);
    if (change.type === "entry") next.set(change.confirmation.confirmationId, change.confirmation);
    else next.delete(change.confirmationId);
    return next;
  },
  initial: INITIAL_CONFIRMATIONS,
});

export const AWAITING_ORDER_DESCRIPTION =
  "Oldest requested first. Ties are ordered by confirmation ID.";
export const DECIDED_ORDER_DESCRIPTION =
  "Newest decisions first. Ties are ordered by confirmation ID.";

export function compareAwaitingApprovals(
  left: DashboardPendingConfirmation,
  right: DashboardPendingConfirmation,
): number {
  return (
    Date.parse(left.requestedAt) - Date.parse(right.requestedAt) ||
    compareConfirmationIds(left.confirmationId, right.confirmationId)
  );
}

export function compareDecidedApprovals(left: DecidedApproval, right: DecidedApproval): number {
  return (
    Date.parse(right.resolvedAt) - Date.parse(left.resolvedAt) ||
    compareConfirmationIds(left.confirmationId, right.confirmationId)
  );
}

function compareConfirmationIds(left: string, right: string): number {
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}

function parseEventData<T>(schema: z.ZodType<T>, data: string): T | undefined {
  try {
    const parsed = schema.safeParse(JSON.parse(data));
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}

export function usePendingApprovals(options: AgentQueryOptions = {}) {
  const api = useHubApi();
  const agents = useAgentsQuery(options);
  // Conversation participants can decide their requests without specialist
  // edit access. This only enables the read; the Gateway filters each request
  // by current conversation participation and rechecks every decision.
  const hasAuthority = agents.data?.agents.some((agent) => agent.viewerCanChat === true) ?? false;
  const enabled = options.enabled ?? true;
  const key =
    enabled && agents.isSuccess && hasAuthority
      ? api.dashboard.pendingConfirmationsEventsUrl()
      : null;
  const snapshot = pendingConfirmationStore.useSnapshot(key);
  const confirmations = useMemo(
    () => [...snapshot.state.values()].sort(compareAwaitingApprovals),
    [snapshot.state],
  );
  const retry = useCallback(() => {
    void agents.refetch();
    snapshot.retry();
  }, [agents.refetch, snapshot.retry]);
  return {
    agents: agents.data?.agents ?? [],
    agentsFailed: agents.isError,
    agentsPending: agents.isPending,
    confirmations,
    hasAuthority,
    isReady: (enabled && agents.isSuccess && !hasAuthority) || snapshot.isReady,
    isFailed: agents.isError || snapshot.isFailed,
    lastGoodAt: snapshot.lastGoodAt,
    retry,
  };
}

export function useDecidedApprovalsQuery(
  options: AgentQueryOptions = {},
  visibleIds: readonly string[] = [],
) {
  const api = useHubApi();
  const me = useMeQuery();
  const storageKey = me.data ? `approval-tracking:${me.data.userId}` : null;
  const [tracking, setTracking] = useState<{ key: string; ids: string[] }>();
  const retainedIds = useMemo(() => {
    if (!storageKey) return [];
    if (tracking?.key === storageKey) return tracking.ids;
    try {
      const parsed = z
        .array(z.string())
        .safeParse(JSON.parse(window.sessionStorage.getItem(storageKey) ?? "[]"));
      return parsed.success ? parsed.data : [];
    } catch {
      return [];
    }
  }, [storageKey, tracking]);
  const confirmationIds = [...new Set([...retainedIds, ...visibleIds])].sort();
  return useQuery({
    queryKey: [...hubQueryKeys.decidedApprovals(), storageKey, confirmationIds],
    queryFn: async () => {
      const data = await api.dashboard.decidedApprovals(confirmationIds);
      const availableIds = new Set([
        ...data.pendingConfirmationIds,
        ...data.queuedApprovals.map((approval) => approval.confirmationId),
        ...data.trackedApprovals.map((approval) => approval.confirmationId),
        ...data.approvals.map((approval) => approval.confirmationId),
      ]);
      const ids = [
        ...new Set([
          ...confirmationIds.filter((id) => availableIds.has(id)),
          ...data.queuedApprovals.map((approval) => approval.confirmationId),
          ...data.approvals
            .filter((approval) => approval.state === "cancelled")
            .map((approval) => approval.confirmationId),
        ]),
      ].sort();
      if (storageKey) {
        try {
          window.sessionStorage.setItem(storageKey, JSON.stringify(ids));
        } catch {
          // Storage can be disabled; the mounted page still tracks the same ids in memory.
        }
        setTracking((current) =>
          current?.key === storageKey && current.ids.join() === ids.join()
            ? current
            : { key: storageKey, ids },
        );
      }
      return { ...data, approvals: [...data.approvals].sort(compareDecidedApprovals) };
    },
    enabled: (options.enabled ?? true) && me.isSuccess,
    placeholderData: keepPreviousData,
    refetchInterval: 5_000,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
  });
}

export interface ApprovalDecisionInput {
  agentId: string;
  sessionId: string;
  confirmationId: string;
  decision: "approve" | "deny";
}

export interface ApprovalDecisionState {
  decision: ApprovalDecisionInput["decision"];
  error?: string;
  inFlight: boolean;
}

export function useResolvePendingConfirmationMutation() {
  const api = useHubApi();
  return useMutation({
    mutationFn: async (input: ApprovalDecisionInput) => {
      const ack = await api.dashboard.resolvePendingConfirmation(input);
      if (ack.accepted) return ack;
      if (ack.reason === "already_resolved") {
        if (ack.prior) return ack;
        throw new Error("This approval was already resolved.");
      }
      if (ack.reason === "agent_paused") {
        throw new Error("This specialist is paused. Resume it before deciding this approval.");
      }
      if (ack.reason === "retryable") {
        throw new Error("The decision could not be saved. Please try again.");
      }
      throw new Error("This approval is no longer available.");
    },
  });
}

export function useApprovalDecision() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  const mutation = useResolvePendingConfirmationMutation();
  const inFlight = useRef(new Set<string>());
  const [states, setStates] = useState<ReadonlyMap<string, ApprovalDecisionState>>(() => new Map());

  const setState = useCallback((confirmationId: string, state?: ApprovalDecisionState) => {
    setStates((current) => {
      const next = new Map(current);
      if (state) next.set(confirmationId, state);
      else next.delete(confirmationId);
      return next;
    });
  }, []);

  const resolve = useCallback(
    async (input: ApprovalDecisionInput, onSuccess?: (ack: HubChatConfirmAck) => void) => {
      if (inFlight.current.has(input.confirmationId)) return false;
      inFlight.current.add(input.confirmationId);
      setState(input.confirmationId, { decision: input.decision, inFlight: true });
      try {
        const ack = await mutation.mutateAsync(input);
        pendingConfirmationStore.apply(api.dashboard.pendingConfirmationsEventsUrl(), {
          type: "remove",
          confirmationId: input.confirmationId,
        });
        void queryClient.invalidateQueries({ queryKey: hubQueryKeys.decidedApprovals() });
        setState(input.confirmationId);
        onSuccess?.(ack);
        return true;
      } catch (error) {
        const message = error instanceof Error ? error.message : "The decision could not be saved.";
        setState(input.confirmationId, {
          decision: input.decision,
          error: message,
          inFlight: false,
        });
        return false;
      } finally {
        inFlight.current.delete(input.confirmationId);
      }
    },
    [api, mutation, queryClient, setState],
  );

  return {
    resolve,
    stateFor: (confirmationId: string) => states.get(confirmationId),
  };
}
